import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Users, AlertTriangle, Ban, CreditCard, Scale } from "lucide-react";

const Terms = () => {
  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-slate-600 hover:text-slate-800">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-bold text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Terms of Service
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Terms of Service</h1>
              <p className="text-slate-500">Last updated: January 2025</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <p className="lead text-lg text-slate-600 mb-8">
              Welcome to KiddiPlay Club! By using our platform, you agree to these terms. Please read them carefully.
            </p>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Users className="w-5 h-5 text-teal-500" />
                What KiddiPlay Club Is (And Isn't)
              </h2>
              <p className="text-slate-600 mb-4">
                KiddiPlay Club is a <strong>social platform</strong> that helps families connect for playdates and shared activities. We are:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>A platform for parents to discover and message other families</li>
                <li>A community where you can share babysitting arrangements</li>
                <li>A directory of family-friendly venues and activities</li>
              </ul>
              <p className="text-slate-600 mt-4">
                We are <strong>NOT</strong>:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>A childcare provider, childminder, or babysitting agency</li>
                <li>A vetting or DBS checking service</li>
                <li>An employer of any carers or sitters</li>
                <li>Responsible for supervising any meetups or playdates</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Eligibility</h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>You must be at least 18 years old to create an account</li>
                <li>You must be a parent, legal guardian, or authorised carer of the children in your profile</li>
                <li>You must provide accurate information about yourself and your family</li>
                <li>You must be a UK resident (KiddiPlay Club is currently UK-only)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <AlertTriangle className="w-5 h-5 text-teal-500" />
                Your Responsibilities
              </h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>You are solely responsible for supervising your children at all times during any meetup</li>
                <li>You must verify the identity of anyone you meet through our platform</li>
                <li>You are responsible for assessing the suitability of any playdate location</li>
                <li>You must not share personal contact details with unverified users</li>
                <li>You must report any safeguarding concerns immediately</li>
                <li>You must comply with all applicable laws regarding childcare</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Ban className="w-5 h-5 text-teal-500" />
                Prohibited Conduct
              </h2>
              <p className="text-slate-600 mb-4">You must not:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Use the platform for any illegal purpose</li>
                <li>Create fake profiles or misrepresent yourself</li>
                <li>Harass, abuse, or threaten other users</li>
                <li>Share inappropriate content or contact details in messages</li>
                <li>Use the platform to find childcare for commercial purposes</li>
                <li>Attempt to bypass our safety features</li>
                <li>Scrape data or use automated tools on our platform</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <CreditCard className="w-5 h-5 text-teal-500" />
                Premium Subscriptions
              </h2>
              <p className="text-slate-600 mb-4">
                KiddiPlay Club offers optional premium features:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Subscriptions are billed monthly via Stripe</li>
                <li>You can cancel at any time - access continues until the end of your billing period</li>
                <li>Refunds are provided in accordance with UK consumer law</li>
                <li>Prices may change with 30 days notice</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Scale className="w-5 h-5 text-teal-500" />
                Limitation of Liability
              </h2>
              <p className="text-slate-600">
                KiddiPlay Club is provided "as is". To the fullest extent permitted by UK law, we are not liable for:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Any incidents that occur during meetups arranged through our platform</li>
                <li>The actions or behaviour of other users</li>
                <li>The accuracy of information provided by users or third-party venues</li>
                <li>Any indirect, consequential, or incidental damages</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Account Termination</h2>
              <p className="text-slate-600">
                We may suspend or terminate your account if you breach these terms. You may delete your account at any time from your profile settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Changes to Terms</h2>
              <p className="text-slate-600">
                We may update these terms from time to time. We will notify you of significant changes via email or in-app notification. Continued use after changes constitutes acceptance.
              </p>
            </section>

            <section className="p-6 bg-teal-50 rounded-xl">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Governing Law</h2>
              <p className="text-slate-600">
                These terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
              <p className="text-slate-600 mt-4">
                <strong>Questions?</strong> Contact us at <a href="mailto:team@kiddiplayclub.com" className="text-teal-600 underline">team@kiddiplayclub.com</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;
